import { z } from 'zod'

import type { PurchaseOrderSort } from '@/client'
import type { ListParams } from '@/hooks/queries/purchase-orders'
import {
  idParam,
  isoDayParam,
  limitParam,
  pageParam,
  startOfDay,
  startOfNextDay,
} from '@/lib/listSearch'

/**
 * The view state the purchase-orders page reads from the URL.
 *
 * Everything that changes what the page shows is here: the filters, the page,
 * the page size, the sort, and `detail`, the PO open in the side panel, so the
 * thing on screen is a link a colleague can open.
 *
 * Every field is optional and every field has `.catch(undefined)`, for the
 * reasons the organizations page spells out: a required field with a default is
 * written into the URL on every navigation, and a query string is user input.
 * Defaults are applied at read time and stripped on the way out.
 *
 * Dates are `yyyy-mm-dd`, which is what `<input type="date">` produces and what
 * a person can read in the address bar. They become the API's datetimes in
 * {@link toListParams}.
 */
const SORTS = [
  '-created',
  'created',
  '-po_date',
  'po_date',
  'code',
  '-code',
  '-amount',
  'amount',
] as const satisfies readonly PurchaseOrderSort[]

export const purchaseOrderSearchSchema = z.object({
  page: pageParam,
  limit: limitParam,
  q: z.string().optional().catch(undefined).describe('Text matched against the PO code.'),
  sku: z
    .string()
    .optional()
    .catch(undefined)
    .describe('A SKU code a purchase order must have a line for.'),
  warehouse: idParam.describe('The warehouse id the purchase orders ship from.'),
  retailer: idParam.describe('The retailer id that placed the purchase orders.'),
  from: isoDayParam.describe('The first day the PO was created, YYYY-MM-DD, inclusive.'),
  to: isoDayParam.describe('The last day the PO was created, YYYY-MM-DD, inclusive.'),
  sort: z
    .enum(SORTS)
    .optional()
    .catch(undefined)
    .describe('The sort key; a leading - is descending.'),
  detail: idParam.describe('The id of the purchase order open in the side sheet.'),
  /** The open PO's free-stock section is shown. */
  stock: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe("Show the open PO's stock availability section."),
  /** The open PO's allotment section is shown. */
  allotment: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe("Show the open PO's allotment section."),
})

export type PurchaseOrderSearch = z.infer<typeof purchaseOrderSearchSchema>

export const PURCHASE_ORDER_SEARCH_DEFAULTS = {
  page: 1,
  limit: 20,
  sort: '-created',
} as const satisfies { page: number; limit: number; sort: PurchaseOrderSort }

/**
 * Drop any value that equals its default, or is empty.
 *
 * An emptied search box is `''`, not "no filter", and TanStack would serialise
 * it as `?q=`. Pruning keeps a URL carrying only what differs, so "is this list
 * filtered?" is answerable at a glance.
 */
export function withoutSearchDefaults(search: PurchaseOrderSearch): PurchaseOrderSearch {
  const pruned: PurchaseOrderSearch = {}
  if (search.page !== undefined && search.page !== PURCHASE_ORDER_SEARCH_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.limit !== undefined && search.limit !== PURCHASE_ORDER_SEARCH_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.sort !== undefined && search.sort !== PURCHASE_ORDER_SEARCH_DEFAULTS.sort) {
    pruned.sort = search.sort
  }
  if (search.q) pruned.q = search.q
  if (search.sku) pruned.sku = search.sku
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.retailer !== undefined) pruned.retailer = search.retailer
  if (search.from) pruned.from = search.from
  if (search.to) pruned.to = search.to
  if (search.detail !== undefined) pruned.detail = search.detail
  if (search.stock) pruned.stock = true
  if (search.allotment) pruned.allotment = true
  return pruned
}

/**
 * What the list endpoint is asked for, from what the URL says.
 *
 * Applied here, in one place, so the page and its tests agree on defaults and
 * on what an inclusive date range means to the API's half-open window.
 */
export function toListParams(search: PurchaseOrderSearch): ListParams {
  return {
    page: search.page ?? PURCHASE_ORDER_SEARCH_DEFAULTS.page,
    limit: search.limit ?? PURCHASE_ORDER_SEARCH_DEFAULTS.limit,
    sort: search.sort ?? PURCHASE_ORDER_SEARCH_DEFAULTS.sort,
    q: search.q || undefined,
    skuCode: search.sku || undefined,
    warehouseId: search.warehouse,
    retailerId: search.retailer,
    createdFrom: search.from ? startOfDay(search.from) : undefined,
    createdTo: search.to ? startOfNextDay(search.to) : undefined,
  }
}
